import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { contractorAuth } from '../../lib/contractorAuth';
import { dueLabel, parseQuickAdd } from '../../lib/quickAdd';
import { DEFAULT_LANGUAGES, languagesFor, readAssistFeatures, voiceAllowed } from '../../lib/todoAssistGate';
import { rememberedLanguage, singleLanguage, spokenToLine, tidyTitle } from '../../lib/voiceDraft';
import { MicButton, VoicePanel, useDictation } from './VoiceTodo';
import { PRIORITIES } from './TodoEditor';
import { Avatar, Dialog, GrowingTextarea, ItTag, PeoplePicker, isContractor, personKey } from './TodoPeople';

/* Priority 4 is no priority at all, and says so (not "P4 · None"). */
const priorityWords = (p) => (Number(p.value) === 4 ? 'No priority' : p.label);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** What a message says, as a first go at a title: one line, not too long. */
export const titleFrom = (m) => {
  const said = String(m?.body || '').replace(/\s+/g, ' ').trim();
  if (said) return said.length > 300 ? `${said.slice(0, 297)}…` : said;
  const file = Array.isArray(m?.files) && m.files[0] ? m.files[0].file_name : null;
  if (file) return `Look at ${file}`;
  return m?.author && !m.mine ? `Follow up with ${m.author}` : 'Follow up';
};

const WHEN_CHIPS = [
  { label: 'Today', words: 'today' },
  { label: 'Tomorrow', words: 'tomorrow' },
  { label: 'Next week', words: 'next week' }
];

/**
 * "MAKE A TO-DO" FROM A CHAT MESSAGE (batch 4).
 *
 * Half of what arrives in chat is something to do - "can you bring the long
 * ladder on Thursday" - and until now it stayed a line in a conversation
 * that scrolled away. So, as Slack's "Remind me about this" and Todoist's
 * "Add as task" do, any message can become a to-do: the title is the message
 * (and can be changed), the day is a chip or a few words read by the same
 * parser as quick add ("fri 3pm"), and it goes into a list of mine - the
 * Inbox unless I say - or to somebody else.
 *
 * FOR WHOM. Me, by default. The other person in a direct thread or a ticket
 * thread is offered beside "Me" - "ring the landlord" from Marco is as often
 * Marco's job as mine - and anybody else in the directory is a search away.
 * Given to somebody, the server puts it in their Inbox (it is sent, as from
 * the list), and I keep sight of it under Delegated.
 *
 * The message is named by its scope and its uuid - the same two things a
 * reaction is saved against - and the to-do keeps a link back to it.
 */
export default function MakeTodo({ message, suggest = [], people = null, now = null, onClose, onMade }) {
  const auth = contractorAuth();
  const [title, setTitle] = useState(() => titleFrom(message));
  const [when, setWhen] = useState('');
  const [priority, setPriority] = useState('4');
  const [lists, setLists] = useState(null);
  const [listId, setListId] = useState('');
  const [unset, setUnset] = useState(false);
  const [forKey, setForKey] = useState('me');
  const [other, setOther] = useState(null);       // somebody found by search
  const [finding, setFinding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const at = useMemo(() => now || new Date(), [now]);

  // Batch C: the mic beside the title, when lib/todoAssistGate.js says so.
  // What is said becomes the title, and any day or time in it the "When".
  const [voice, setVoice] = useState(() => readAssistFeatures(null));
  const [voiceLang, setVoiceLang] = useState(() => rememberedLanguage(DEFAULT_LANGUAGES));
  useEffect(() => {
    let live = true;
    axios.get(`${API_BASE}/todos/features`, auth)
      .then((r) => {
        if (!live) return;
        const f = readAssistFeatures(r.data);
        setVoice(f);
        // Auto (Malti + English) is the default where AI help is on.
        setVoiceLang(rememberedLanguage(f.voice_languages, { ai: f.ai_assist }));
      })
      .catch(() => {});
    return () => { live = false; };
    // Once, when it opens, as the lists are.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const sendVoice = useCallback((blob, language) => axios.post(
    `${API_BASE}/todos/voice?language=${encodeURIComponent(language)}&fallback=${encodeURIComponent(singleLanguage(DEFAULT_LANGUAGES))}`, blob,
    { ...(auth || {}), headers: { ...((auth && auth.headers) || {}), 'Content-Type': 'audio/wav' } }).then((r) => r.data),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const dictation = useDictation({
    send: sendVoice, maxSeconds: voice.voice_max_seconds, language: voiceLang, onError: setProblem,
    onText: (got) => {
      const line = spokenToLine(got?.text || '');
      const p = parseQuickAdd(line, { now: at });
      const said = p.chips.filter((c) => c.kind === 'date' || c.kind === 'time').map((c) => c.text).join(' ');
      setTitle(tidyTitle(p.title) || tidyTitle(line) || title);
      if (said) setWhen(said);
    }
  });
  const canVoice = voiceAllowed(voice) && !unset;

  useEffect(() => {
    let live = true;
    axios.get(`${API_BASE}/todos/lists`, auth)
      .then((r) => {
        if (!live) return;
        const ls = (Array.isArray(r.data) ? r.data : []).filter((l) => l && l.id && !l.archived_at);
        setLists(ls);
        setListId((ls.find((l) => l.is_inbox) || ls[0] || {}).id || '');
      })
      .catch((err) => {
        if (!live) return;
        setLists([]);
        if (err.response?.status === 503) setUnset(true);
      });
    return () => { live = false; };
    // Once, when it opens: the token does not change under an open dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const read = useMemo(() => parseQuickAdd(when, { now: at }), [when, at]);
  const dueDate = read.due_date;
  const dueTime = read.due_time;
  const understood = when.trim() && (dueDate || dueTime);

  const offered = suggest.filter((p) => p && personKey(p)).map((p) => ({ ...p, key: personKey(p) }));
  const chosen = forKey === 'me' ? null
    : offered.find((p) => p.key === forKey) || (other && other.key === forKey ? other : null);

  const submit = async (e) => {
    e.preventDefault();
    const t = title.trim();
    if (!t || busy) return;
    if (when.trim() && !understood) { setProblem(`"${when.trim()}" is not a day this understands - try "fri" or "25/10 3pm".`); return; }
    const body = { scope: message.scope, id: message.id, title: t, priority: Number(priority) || 4 };
    if (listId) body.list_id = listId;
    if (dueDate) body.due_date = dueDate;
    if (dueTime) body.due_time = dueTime;
    if (chosen) body.assignee_key = chosen.key;
    setBusy(true);
    setProblem(null);
    try {
      const r = await axios.post(`${API_BASE}/todos/from-message`, body, auth);
      onMade(r.data || {}, chosen);
    } catch (err) {
      setProblem(err.response?.status === 503 ? 'To-dos are not set up on this server yet.'
        : err.response?.data?.error || 'That to-do could not be made');
      setBusy(false);
    }
  };

  return (
    <Dialog label="Make a to-do" onClose={onClose} className="mt-dialog">
      {unset ? (
        <>
          <p className="tds-quiet">To-dos are not set up on this server yet. Nothing needs doing on your side.</p>
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-primary tds-btn" onClick={onClose}>Close</button>
          </div>
        </>
      ) : (
        <form className="mt-form" onSubmit={submit}>
          {problem && <p className="tds-error" role="alert">{problem}</p>}
          {dictation.busy ? (
            <div className="tds-field">
              <span>To-do</span>
              <VoicePanel dictation={dictation} languages={languagesFor(voice)} language={voiceLang} onLanguage={setVoiceLang} />
            </div>
          ) : (
            <label className="tds-field">
              <span>To-do</span>
              <span className={canVoice ? 'mt-voice' : undefined}>
                <GrowingTextarea rows={2} value={title} maxLength={300} aria-label="To-do" autoFocus
                                 onChange={(e) => setTitle(e.target.value)}
                                 onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); } }} />
                {canVoice && <MicButton dictation={dictation} disabled={busy} label="Dictate it" />}
              </span>
            </label>
          )}

          <div className="tds-field">
            <span>When</span>
            <div className="mt-chips" role="group" aria-label="When">
              {WHEN_CHIPS.map((c) => (
                <button key={c.words} type="button" className="mt-chip" aria-pressed={when === c.words}
                        onClick={() => setWhen(when === c.words ? '' : c.words)}>{c.label}</button>
              ))}
            </div>
            <input type="text" value={when} aria-label="Or type a day" placeholder='Or type it: "fri 3pm", "25/10"'
                   onChange={(e) => setWhen(e.target.value)} />
            {understood && (
              <span className="mt-when" data-due={`${dueDate || ''} ${dueTime || ''}`.trim()}>
                Due <strong>{dueLabel(dueDate, dueTime, at)}</strong>
              </span>
            )}
          </div>

          <div className="mt-row">
            <label className="tds-field">
              <span>List</span>
              <select aria-label="List" value={listId} disabled={!lists || !lists.length}
                      onChange={(e) => setListId(e.target.value)}>
                {lists === null && <option value="">Loading…</option>}
                {(lists || []).map((l) => (
                  // 089: nothing in Private is given to anybody - with a person chosen, it is not offered.
                  <option key={l.id} value={l.id} disabled={Boolean(l.is_private && chosen)}>
                    {l.is_private ? `${l.name} (private)` : l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="tds-field">
              <span>Priority</span>
              <select aria-label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((p) => <option key={p.value} value={String(p.value)}>{priorityWords(p)}</option>)}
              </select>
            </label>
          </div>

          <div className="mt-for">
            <span className="mt-for-label" id="mt-for-label">For</span>
            <div className="mt-chips" role="group" aria-labelledby="mt-for-label">
              <button type="button" className="mt-chip" aria-pressed={forKey === 'me'}
                      onClick={() => { setForKey('me'); setFinding(false); }}>Me</button>
              {offered.map((p) => (
                <button key={p.key} type="button" className="mt-chip" aria-pressed={forKey === p.key}
                        data-for={p.key} onClick={() => { setForKey(p.key); setFinding(false); }}>
                  {p.name}
                </button>
              ))}
              {other && !offered.some((p) => p.key === other.key) && (
                <button type="button" className="mt-chip" aria-pressed={forKey === other.key} data-for={other.key}
                        onClick={() => setForKey(other.key)}>{other.name}</button>
              )}
              <button type="button" className="mt-chip is-search" aria-expanded={finding}
                      onClick={() => setFinding((v) => !v)}>Someone else…</button>
            </div>
            {finding && (
              <PeoplePicker people={people} autoFocus label="For whom"
                            exclude={new Set(offered.map((p) => p.key))}
                            onPick={(p) => { setOther(p); setForKey(p.key); setFinding(false); }} />
            )}
            {chosen && (
              <p className="tds-chosen tds-quiet">
                <Avatar name={chosen.name} who={chosen.key} />
                <span>
                  For {chosen.name}. In a list they are not in, it goes to their Inbox and you follow it
                  under Delegated.
                </span>
                {isContractor(chosen) && <ItTag />}
              </p>
            )}
          </div>

          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary tds-btn" disabled={busy || !title.trim() || unset}>
              Add to-do
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** "To-do added · Open", for six seconds, on whichever page made it. */
export function TodoToast({ text, onOpen, onDone }) {
  // The page re-renders under it (chat polls); the six seconds must not restart each time.
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const t = setTimeout(() => done.current(), 6000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="tds-toast" role="status">
      <span>{text}</span>
      {onOpen && <button type="button" className="tds-link" onClick={onOpen}>Open</button>}
    </div>
  );
}
